import {
    BaseButton,
    BaseCheckbox,
    BaseSelect,
    ISelectOption,
    ProviderPayerSubscriber,
    ProviderSubscriberPatient,
} from '@enter/core';
import React, { useMemo, useState } from 'react';
import { IDialogContextProps } from 'services';
import {
    findSubscriberPatientFor,
    getPayerSubscriberLabel,
    getSubscriberPatientLabel,
} from '../claim-timeline-table/cms1500-selection';
import * as S from './cms1500-dialog.styles';

export interface ICms1500Selection {
    payerSubscriberId: string;
    subscriberPatientId: string;
    editable: boolean;
}

interface ICms1500DialogProps {
    payerSubscribers: ProviderPayerSubscriber[];
    subscriberPatients: ProviderSubscriberPatient[];
    defaultPayerSubscriberId?: string;
    defaultSubscriberPatientId?: string;
    onConfirm: (selection: ICms1500Selection) => void;
    onCancel: () => void;
}

export const Cms1500Dialog: React.FC<ICms1500DialogProps> = ({
    payerSubscribers,
    subscriberPatients,
    defaultPayerSubscriberId,
    defaultSubscriberPatientId,
    onConfirm,
    onCancel,
}) => {
    const payerOptions: ISelectOption<string>[] = useMemo(
        () =>
            payerSubscribers
                .filter(el => !!el.id)
                .map((el, index) => ({
                    value: el.id,
                    label: getPayerSubscriberLabel(el, payerSubscribers.length > 1 ? index : undefined),
                })),
        [payerSubscribers],
    );
    const patientOptions: ISelectOption<string>[] = useMemo(
        () =>
            subscriberPatients
                .filter(el => !!el.id)
                .map(el => ({ value: el.id, label: getSubscriberPatientLabel(el) })),
        [subscriberPatients],
    );

    const patientFor = (payerSubscriberId?: string): string | undefined =>
        findSubscriberPatientFor(
            payerSubscribers.find(el => el.id === payerSubscriberId),
            payerSubscribers,
            subscriberPatients,
        )?.id;

    // A requested default that no longer exists (e.g. the transmission's payer was removed) is left unselected
    // rather than replaced with another payer's form.
    const initialPayerId = defaultPayerSubscriberId
        ? payerSubscribers.find(el => !!el.id && el.id === defaultPayerSubscriberId)?.id
        : payerOptions[0]?.value;
    const [payerSubscriberId, setPayerSubscriberId] = useState<string | undefined>(initialPayerId);
    const [subscriberPatientId, setSubscriberPatientId] = useState<string | undefined>(
        subscriberPatients.find(el => !!el.id && el.id === defaultSubscriberPatientId)?.id ||
            patientFor(initialPayerId),
    );
    const [editable, setEditable] = useState(false);

    const canView = !!payerSubscriberId && !!subscriberPatientId;

    return (
        <S.Body>
            <S.Field>
                <S.Label>Payer subscriber</S.Label>
                <BaseSelect
                    required
                    placeholder="Select payer subscriber"
                    options={payerOptions}
                    value={payerOptions.find(el => el.value === payerSubscriberId) || null}
                    onChange={option => {
                        const id = (option as ISelectOption<string> | null)?.value;
                        setPayerSubscriberId(id);
                        // The relationship follows the subscriber; the user can still change it.
                        setSubscriberPatientId(patientFor(id));
                    }}
                />
            </S.Field>
            <S.Field>
                <S.Label>Subscriber patient</S.Label>
                <BaseSelect
                    required
                    placeholder="Select subscriber patient"
                    options={patientOptions}
                    value={patientOptions.find(el => el.value === subscriberPatientId) || null}
                    onChange={option => setSubscriberPatientId((option as ISelectOption<string> | null)?.value)}
                />
            </S.Field>
            <BaseCheckbox
                label="Unflattened"
                checked={editable}
                onChange={event => setEditable(event.target.checked)}
            />
            {payerSubscriberId && !subscriberPatientId && (
                <S.Note>No subscriber patient matches this payer subscriber. Select one to continue.</S.Note>
            )}
            <S.Actions>
                <BaseButton variant="outlined" size="medium" type="button" onClick={onCancel}>
                    Cancel
                </BaseButton>
                <BaseButton
                    variant="primary"
                    size="medium"
                    type="button"
                    disabled={!canView}
                    onClick={() => canView && onConfirm({ payerSubscriberId, subscriberPatientId, editable })}>
                    View CMS-1500
                </BaseButton>
            </S.Actions>
        </S.Body>
    );
};

export interface IShowCms1500DialogOptions {
    payerSubscribers: ProviderPayerSubscriber[];
    subscriberPatients: ProviderSubscriberPatient[];
    defaultPayerSubscriberId?: string;
    defaultSubscriberPatientId?: string;
    onConfirm: (selection: ICms1500Selection) => void;
    /** Called when the dialog is dismissed without a selection. */
    onCancel?: () => void;
}

export function showCms1500Dialog(dialogContext: IDialogContextProps, options: IShowCms1500DialogOptions): void {
    const cancel = (): void => {
        dialogContext.hideDialog();
        options.onCancel?.();
    };

    dialogContext.showDialog({
        props: {
            onClose: cancel,
            renderTitle: 'View CMS-1500',
            showCloseButton: true,
            maxWidth: 'sm',
            fullWidth: true,
        },
        Component: (
            <Cms1500Dialog
                payerSubscribers={options.payerSubscribers}
                subscriberPatients={options.subscriberPatients}
                defaultPayerSubscriberId={options.defaultPayerSubscriberId}
                defaultSubscriberPatientId={options.defaultSubscriberPatientId}
                onCancel={cancel}
                onConfirm={selection => {
                    dialogContext.hideDialog();
                    options.onConfirm(selection);
                }}
            />
        ),
    });
}
